<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  Search,
  LayoutDashboard,
  Kanban,
  ShoppingBag,
  ScanLine,
  BarChart3,
  Package,
  Settings as SettingsIcon,
  User as UserIcon,
  Phone,
  Mail,
  Moon,
  Sun,
  Monitor,
  Bell,
  Target,
  ArrowRight,
  CornerDownLeft,
  Sparkles,
} from 'lucide-vue-next';
import { useTheme, type Theme } from '@/composables/useTheme';
import { useCustomer360 } from '@/composables/useCustomer360';
import { CUSTOMER_PROFILES } from '@/data/customer-profiles';
import { useToast } from '@/composables/useToast';

/**
 * Command Palette (Cmd+K / Ctrl+K) — globalna nawigacja + akcje.
 *
 * Sekcje:
 * - Nawigacja: skok do widoku
 * - Klienci: Customer 360 dla znanych klientow
 * - Akcje: zmiana motywu, wlacz powiadomienia, etc.
 *
 * Fuzzy search (substring match z highlight). Strzalki gora/dol + Enter.
 * Esc zamyka.
 */

const router = useRouter();
const { theme, set: setTheme } = useTheme();
const { openCustomer } = useCustomer360();
const toast = useToast();

const visible = ref(false);
const query = ref('');
const selectedIndex = ref(0);
const inputRef = ref<HTMLInputElement | null>(null);

type CommandItem = {
  id: string;
  label: string;
  hint?: string;
  group: 'nav' | 'customer' | 'action' | 'theme';
  icon: typeof LayoutDashboard;
  iconColor?: string;
  keywords?: string;
  action: () => void;
};

const allCommands = computed<CommandItem[]>(() => {
  const items: CommandItem[] = [];

  // Nawigacja
  items.push(
    {
      id: 'nav-dashboard',
      label: 'Pulpit',
      hint: 'Hero KPI, mapa, oddzialy',
      group: 'nav',
      icon: LayoutDashboard,
      iconColor: 'var(--color-product-light)',
      keywords: 'dashboard glowna home',
      action: () => router.push('/dashboard'),
    },
    {
      id: 'nav-pipeline',
      label: 'Pipeline',
      hint: 'Lejek sprzedazowy, drag & drop',
      group: 'nav',
      icon: Kanban,
      iconColor: '#8B5CF6',
      keywords: 'lead leady kanban',
      action: () => router.push('/pipeline'),
    },
    {
      id: 'nav-stock',
      label: 'Stock',
      hint: 'Inventory + aging',
      group: 'nav',
      icon: Package,
      iconColor: 'var(--color-warning)',
      keywords: 'inwentarz auta stocky magazyn',
      action: () => router.push('/stock'),
    },
    {
      id: 'nav-sales',
      label: 'Sprzedaz',
      hint: 'Lista transakcji + quick entry',
      group: 'nav',
      icon: ShoppingBag,
      iconColor: 'var(--color-success)',
      keywords: 'sales sprzedaze transakcje',
      action: () => router.push('/sales'),
    },
    {
      id: 'nav-scan',
      label: 'Skanowanie dokumentu',
      hint: 'OCR + auto-fill lead',
      group: 'nav',
      icon: ScanLine,
      iconColor: '#EC4899',
      keywords: 'skan dokument ocr foto',
      action: () => router.push('/scan'),
    },
    {
      id: 'nav-reports',
      label: 'Raporty',
      hint: 'LineChart, Funnel, Heatmap, Mapa PL',
      group: 'nav',
      icon: BarChart3,
      iconColor: 'var(--color-info)',
      keywords: 'reports analytics analytics wykresy',
      action: () => router.push('/reports'),
    },
    {
      id: 'nav-settings',
      label: 'Ustawienia',
      hint: 'Profil, motyw, powiadomienia',
      group: 'nav',
      icon: SettingsIcon,
      iconColor: 'var(--surface-fg-muted)',
      keywords: 'settings ustawienia konto',
      action: () => router.push('/settings'),
    },
  );

  // Klienci (Customer 360)
  for (const [name, profile] of Object.entries(CUSTOMER_PROFILES)) {
    items.push({
      id: `customer-${name}`,
      label: profile.name,
      hint: `${profile.tier === 'vip' ? 'VIP · ' : ''}${profile.phone ?? profile.email ?? 'klient'}`,
      group: 'customer',
      icon: UserIcon,
      iconColor: profile.tier === 'vip' ? 'var(--color-accent)' : 'var(--color-product-light)',
      keywords: `klient customer ${profile.email ?? ''} ${profile.phone ?? ''}`,
      action: () => openCustomer(name),
    });
  }

  // Akcje
  items.push(
    {
      id: 'action-call',
      label: 'Zadzwon do ostatniego klienta',
      hint: 'Dial number z clipboard',
      group: 'action',
      icon: Phone,
      iconColor: 'var(--color-product-light)',
      keywords: 'call telefon zadzwon',
      action: () => toast.info('Telefon', 'Faza 1: integracja z VOIP'),
    },
    {
      id: 'action-email',
      label: 'Wyslij email do zespolu',
      hint: 'Compose new message',
      group: 'action',
      icon: Mail,
      iconColor: 'var(--color-info)',
      keywords: 'email mail wyslij',
      action: () => toast.info('Email', 'Otwarcie nowej wiadomosci'),
    },
    {
      id: 'action-tour',
      label: 'Pokaz tour ponownie',
      hint: '4-slajdy onboarding',
      group: 'action',
      icon: Sparkles,
      iconColor: 'var(--color-accent)',
      keywords: 'tour onboarding pomoc',
      action: () => {
        if (typeof localStorage !== 'undefined') localStorage.removeItem('moredeal.tour-seen');
        if (router.currentRoute.value.path === '/dashboard') {
          window.location.reload();
        } else {
          router.push('/dashboard').then(() => window.location.reload());
        }
      },
    },
    {
      id: 'action-goals',
      label: 'Ustaw cele oddzialu',
      hint: 'Otworz Dashboard → klik "Cel" przy oddziale',
      group: 'action',
      icon: Target,
      iconColor: 'var(--color-warning)',
      keywords: 'cele cel goal target',
      action: () => router.push('/dashboard'),
    },
    {
      id: 'action-notifications',
      label: 'Wlacz powiadomienia',
      hint: 'Browser push notifications',
      group: 'action',
      icon: Bell,
      iconColor: 'var(--color-product-light)',
      keywords: 'notyfikacje powiadomienia push',
      action: async () => {
        if ('Notification' in window) {
          const result = await Notification.requestPermission();
          if (result === 'granted') toast.success('Powiadomienia', 'Wlaczone');
          else toast.warning('Powiadomienia', 'Nieprzyznane przez przegladarke');
        }
      },
    },
  );

  // Motyw
  const themes: { value: Theme; label: string; icon: typeof LayoutDashboard }[] = [
    { value: 'light', label: 'Motyw: jasny', icon: Sun },
    { value: 'dark', label: 'Motyw: ciemny', icon: Moon },
    { value: 'system', label: 'Motyw: systemowy', icon: Monitor },
  ];
  for (const t of themes) {
    items.push({
      id: `theme-${t.value}`,
      label: t.label,
      hint: theme.value === t.value ? 'Aktywny' : undefined,
      group: 'theme',
      icon: t.icon,
      iconColor: 'var(--surface-fg-muted)',
      keywords: `motyw theme ${t.value}`,
      action: () => {
        setTheme(t.value);
        toast.info('Motyw zmieniony', t.label);
      },
    });
  }

  return items;
});

// Fuzzy search: substring match na label + keywords
function score(item: CommandItem, q: string): number {
  if (!q) return 1;
  const lower = q.toLowerCase();
  const haystack = `${item.label} ${item.hint ?? ''} ${item.keywords ?? ''}`.toLowerCase();
  if (haystack.includes(lower)) {
    // Boost dla matchy na poczatku label
    if (item.label.toLowerCase().startsWith(lower)) return 3;
    return 2;
  }
  return 0;
}

const filtered = computed(() => {
  const q = query.value.trim();
  const scored = allCommands.value.map((c) => ({ c, s: score(c, q) })).filter((x) => x.s > 0);
  scored.sort((a, b) => b.s - a.s);
  return scored.map((x) => x.c);
});

const grouped = computed(() => {
  const groups: Record<CommandItem['group'], CommandItem[]> = {
    nav: [],
    customer: [],
    action: [],
    theme: [],
  };
  for (const item of filtered.value) groups[item.group].push(item);
  return groups;
});

const flatIndex = computed(() => filtered.value);

const groupLabels: Record<CommandItem['group'], string> = {
  nav: 'Nawigacja',
  customer: 'Klienci (Customer 360)',
  action: 'Akcje',
  theme: 'Motyw',
};

function open() {
  visible.value = true;
  query.value = '';
  selectedIndex.value = 0;
  nextTick(() => inputRef.value?.focus());
}

function close() {
  visible.value = false;
}

function executeSelected() {
  const item = flatIndex.value[selectedIndex.value];
  if (!item) return;
  close();
  item.action();
}

function onKey(e: KeyboardEvent) {
  // Globalny shortcut Cmd+K / Ctrl+K
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    visible.value ? close() : open();
    return;
  }
  if (!visible.value) return;
  if (e.key === 'Escape') {
    e.preventDefault();
    close();
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    selectedIndex.value = Math.min(selectedIndex.value + 1, flatIndex.value.length - 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    selectedIndex.value = Math.max(selectedIndex.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    executeSelected();
  }
}

watch(query, () => {
  selectedIndex.value = 0;
});

onMounted(() => {
  window.addEventListener('keydown', onKey);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKey);
});

defineExpose({ open, close });

function indexInFlat(item: CommandItem): number {
  return flatIndex.value.indexOf(item);
}
</script>

<template>
  <Teleport to="body">
    <Transition name="palette-fade">
      <div
        v-if="visible"
        class="fixed inset-0 z-[300] flex items-start justify-center pt-20 px-4"
        :style="{ background: 'rgba(0, 0, 0, 0.55)', backdropFilter: 'blur(8px)' }"
        @click.self="close"
      >
        <Transition name="palette-slide">
          <div
            v-if="visible"
            class="w-full max-w-xl card card-featured overflow-hidden"
            :style="{ boxShadow: '0 24px 64px rgba(0, 0, 0, 0.4)' }"
            @click.stop
          >
            <!-- Search input -->
            <div
              class="flex items-center gap-3 px-4 py-3"
              :style="{ borderBottom: '1px solid var(--border-soft)' }"
            >
              <Search :size="18" :style="{ color: 'var(--surface-fg-muted)' }" />
              <input
                ref="inputRef"
                v-model="query"
                type="text"
                class="flex-1 bg-transparent outline-none text-base"
                placeholder="Szukaj akcji, klienta, widoku..."
                aria-label="Szukaj"
              />
              <kbd
                class="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono"
                :style="{
                  background: 'var(--card-bg-glass)',
                  border: '1px solid var(--border-soft)',
                  color: 'var(--surface-fg-2)',
                }"
              >
                Esc
              </kbd>
            </div>

            <!-- Results -->
            <div class="max-h-[60vh] overflow-y-auto py-1">
              <div
                v-if="filtered.length === 0"
                class="px-4 py-8 text-center"
                :style="{ color: 'var(--surface-fg-faint)' }"
              >
                <Search :size="20" class="mx-auto mb-2 opacity-50" />
                <p class="text-sm">Brak wynikow dla "{{ query }}"</p>
              </div>

              <template v-else>
                <template v-for="(items, group) in grouped" :key="group">
                  <div v-if="items.length > 0">
                    <p
                      class="text-[10px] uppercase tracking-wider px-4 pt-3 pb-1"
                      :style="{ color: 'var(--surface-fg-faint)' }"
                    >
                      {{ groupLabels[group] }}
                    </p>
                    <button
                      v-for="item in items"
                      :key="item.id"
                      type="button"
                      class="w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors"
                      :class="{
                        'cmd-item-active': indexInFlat(item) === selectedIndex,
                      }"
                      @click="
                        () => {
                          selectedIndex = indexInFlat(item);
                          executeSelected();
                        }
                      "
                      @mouseenter="selectedIndex = indexInFlat(item)"
                    >
                      <div
                        class="w-8 h-8 rounded-card flex items-center justify-center shrink-0"
                        :style="{
                          background: `color-mix(in srgb, ${item.iconColor ?? 'var(--surface-fg)'} 14%, transparent)`,
                          color: item.iconColor ?? 'var(--surface-fg)',
                        }"
                      >
                        <component :is="item.icon" :size="14" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-semibold truncate">{{ item.label }}</p>
                        <p
                          v-if="item.hint"
                          class="text-xs truncate"
                          :style="{ color: 'var(--surface-fg-faint)' }"
                        >
                          {{ item.hint }}
                        </p>
                      </div>
                      <CornerDownLeft
                        v-if="indexInFlat(item) === selectedIndex"
                        :size="14"
                        :style="{ color: 'var(--surface-fg-faint)' }"
                      />
                      <ArrowRight
                        v-else
                        :size="14"
                        :style="{ color: 'var(--surface-fg-faint)' }"
                        class="opacity-50"
                      />
                    </button>
                  </div>
                </template>
              </template>
            </div>

            <!-- Footer hints -->
            <div
              class="flex items-center justify-between px-4 py-2 text-[11px]"
              :style="{
                borderTop: '1px solid var(--border-soft)',
                background: 'var(--card-bg-glass)',
                color: 'var(--surface-fg-faint)',
              }"
            >
              <div class="flex items-center gap-3">
                <span class="inline-flex items-center gap-1">
                  <kbd
                    class="px-1.5 py-0.5 rounded text-[10px]"
                    :style="{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-soft)',
                    }"
                    >↑</kbd
                  >
                  <kbd
                    class="px-1.5 py-0.5 rounded text-[10px]"
                    :style="{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-soft)',
                    }"
                    >↓</kbd
                  >
                  Nawigacja
                </span>
                <span class="inline-flex items-center gap-1">
                  <kbd
                    class="px-1.5 py-0.5 rounded text-[10px]"
                    :style="{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-soft)',
                    }"
                    >↵</kbd
                  >
                  Wybierz
                </span>
              </div>
              <span>{{ filtered.length }} {{ filtered.length === 1 ? 'wynik' : 'wynikow' }}</span>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.cmd-item-active {
  background: var(--card-bg-glass);
}

.palette-fade-enter-active,
.palette-fade-leave-active {
  transition: opacity 0.2s var(--ease-smooth);
}
.palette-fade-enter-from,
.palette-fade-leave-to {
  opacity: 0;
}

.palette-slide-enter-active,
.palette-slide-leave-active {
  transition:
    opacity 0.25s var(--ease-smooth),
    transform 0.3s var(--ease-spring);
}
.palette-slide-enter-from,
.palette-slide-leave-to {
  opacity: 0;
  transform: translateY(-12px) scale(0.97);
}

kbd {
  font-family: ui-monospace, monospace;
}
</style>
